import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import UserAvatar from './UserAvatar';
import {
  FileText, Plus, Sparkles, Send, CheckCircle2, Zap, AlertTriangle,
  MessageSquare, Clock, Loader2, Tag, Trash2, Edit2, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Note type configuration
const NOTE_TYPES = [
  { value: 'general', label: 'General', icon: MessageSquare, color: 'bg-slate-600' },
  { value: 'decision', label: 'Decision', icon: CheckCircle2, color: 'bg-green-600' },
  { value: 'action', label: 'Action', icon: Zap, color: 'bg-blue-600' },
  { value: 'issue', label: 'Issue', icon: AlertTriangle, color: 'bg-orange-600' }
];

// Section labels
const SECTION_LABELS = {
  segue: 'Segue',
  scorecard: 'Scorecard Review',
  rocks: 'Rock Review',
  headlines: 'Headlines',
  todos: 'To-Do List',
  ids: 'IDS',
  conclude: 'Conclude'
};

/**
 * L10 Video Notes - In-video note taking with AI integration
 * Context-aware, timestamped, and shareable notes
 */
const L10VideoNotes = ({
  meetingId,
  currentSection,
  currentUserId,
  copilotEnabled = false,
  notes = [],
  onNotesUpdate
}) => {
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [filterSection, setFilterSection] = useState('current');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const textareaRef = useRef(null);
  const [meetingStartTime] = useState(Date.now());
  
  // Get token for auth
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  
  // Get current user info
  const getCurrentUser = () => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  };
  
  // Format timestamp relative to meeting start
  const formatTimestamp = (timestamp) => {
    const elapsed = Math.floor((new Date(timestamp).getTime() - meetingStartTime) / 1000);
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // Filter notes based on selection
  const filteredNotes = notes.filter(note => {
    if (filterSection === 'current') return note.section === currentSection;
    if (filterSection === 'all') return true;
    return note.section === filterSection;
  });
  
  // Handle submit note
  const handleSubmitNote = async () => {
    if (!newNote.trim()) return;
    
    setIsSubmitting(true);
    const user = getCurrentUser();
    
    const noteData = {
      content: newNote.trim(),
      type: noteType,
      section: currentSection,
      author_id: currentUserId || user.id,
      author_name: user.first_name ? `${user.first_name} ${user.last_name}` : user.email,
      timestamp: new Date().toISOString()
    };
    
    try {
      const response = await axios.post(
        `${API}/eos/meetings/${meetingId}/video-notes`,
        noteData,
        { headers: getAuthHeaders() }
      );
      
      // Update local state
      onNotesUpdate?.([...notes, { ...noteData, id: response.data.id || Date.now().toString() }]);
      setNewNote('');
      setNoteType('general');
      toast.success('Note added');
    } catch (error) {
      console.error('Error adding note:', error);
      // Optimistic update even on error
      onNotesUpdate?.([...notes, { ...noteData, id: Date.now().toString() }]);
      setNewNote('');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle generate AI notes
  const handleGenerateAINotes = async () => {
    if (!copilotEnabled) {
      toast.error('Enable AI Copilot to generate notes');
      return;
    }
    
    setIsGeneratingAI(true);
    
    try {
      const response = await axios.post(
        `${API}/eos/meetings/${meetingId}/copilot/generate-notes-from-video`,
        {
          section: currentSection,
          existing_notes: notes.filter(n => n.section === currentSection),
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.generated_notes) {
        // Add AI-generated notes
        const aiNotes = response.data.generated_notes.map((note, idx) => ({
          id: `ai-${Date.now()}-${idx}`,
          content: note.content,
          type: note.type || 'general',
          section: currentSection,
          author_id: 'ai-copilot',
          author_name: 'AI Copilot',
          timestamp: new Date().toISOString(),
          is_ai_generated: true
        }));
        
        onNotesUpdate?.([...notes, ...aiNotes]);
        toast.success(`Generated ${aiNotes.length} AI notes`);
      }
    } catch (error) {
      console.error('Error generating AI notes:', error);
      toast.error('Failed to generate AI notes');
    } finally {
      setIsGeneratingAI(false);
    }
  };
  
  // Handle delete note
  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(
        `${API}/eos/meetings/${meetingId}/video-notes/${noteId}`,
        { headers: getAuthHeaders() }
      );
      
      onNotesUpdate?.(notes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      console.error('Error deleting note:', error);
      // Still remove locally
      onNotesUpdate?.(notes.filter(n => n.id !== noteId));
    }
  };
  
  // Handle edit note
  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditContent(note.content);
  };
  
  // Handle save edit
  const handleSaveEdit = async (noteId) => {
    if (!editContent.trim()) return;
    
    try {
      await axios.patch(
        `${API}/eos/meetings/${meetingId}/video-notes/${noteId}`,
        { content: editContent.trim() },
        { headers: getAuthHeaders() }
      );
      
      onNotesUpdate?.(notes.map(n => 
        n.id === noteId ? { ...n, content: editContent.trim() } : n
      ));
      setEditingNoteId(null);
      setEditContent('');
      toast.success('Note updated');
    } catch (error) {
      console.error('Error updating note:', error);
      // Still update locally
      onNotesUpdate?.(notes.map(n => 
        n.id === noteId ? { ...n, content: editContent.trim() } : n
      ));
      setEditingNoteId(null);
    }
  };
  
  // Get note type config
  const getNoteTypeConfig = (type) => {
    return NOTE_TYPES.find(t => t.value === type) || NOTE_TYPES[0];
  };
  
  return (
    <div className="flex flex-col h-full bg-slate-900">
      {/* Header */}
      <div className="p-2 border-b border-slate-800">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-slate-400" />
            <span className="text-sm font-medium text-white">Meeting Notes</span>
            <Badge variant="outline" className="text-[10px] border-slate-600">
              {SECTION_LABELS[currentSection] || currentSection}
            </Badge>
          </div>
          
          {/* Filter */}
          <Select value={filterSection} onValueChange={setFilterSection}>
            <SelectTrigger className="w-[100px] h-7 text-xs bg-slate-800 border-slate-700">
              <Filter className="h-3 w-3 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="current">Current</SelectItem>
              <SelectItem value="all">All</SelectItem>
              {Object.entries(SECTION_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* AI Generate Button */}
        {copilotEnabled && (
          <Button
            variant="outline"
            size="sm"
            className="w-full bg-purple-900/30 border-purple-700 hover:bg-purple-900/50 text-purple-300"
            onClick={handleGenerateAINotes}
            disabled={isGeneratingAI}
          >
            {isGeneratingAI ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Generate AI Notes from Video
          </Button>
        )}
      </div>
      
      {/* Notes List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-2">
          {filteredNotes.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notes yet</p>
              <p className="text-xs">Add notes below or generate with AI</p>
            </div>
          ) : (
            filteredNotes.map(note => {
              const typeConfig = getNoteTypeConfig(note.type);
              const TypeIcon = typeConfig.icon;
              const isEditing = editingNoteId === note.id;
              
              return (
                <div
                  key={note.id}
                  className={`p-2 rounded-lg border ${
                    note.is_ai_generated 
                      ? 'bg-purple-900/20 border-purple-800' 
                      : 'bg-slate-800 border-slate-700'
                  }`}
                >
                  {/* Note Header */}
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`${typeConfig.color} text-[10px] py-0 px-1`}>
                      <TypeIcon className="h-2.5 w-2.5 mr-0.5" />
                      {typeConfig.label}
                    </Badge>
                    
                    {note.section !== currentSection && (
                      <Badge variant="outline" className="text-[10px] py-0 border-slate-600">
                        {SECTION_LABELS[note.section]}
                      </Badge>
                    )}
                    
                    {note.is_ai_generated && (
                      <Badge className="bg-purple-600 text-[10px] py-0">
                        <Sparkles className="h-2 w-2 mr-0.5" />
                        AI
                      </Badge>
                    )}
                    
                    <span className="text-[10px] text-slate-500 ml-auto flex items-center gap-1">
                      <Clock className="h-2.5 w-2.5" />
                      {formatTimestamp(note.timestamp)}
                    </span>
                  </div>
                  
                  {/* Note Content */}
                  {isEditing ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="text-xs bg-slate-700 border-slate-600 min-h-[60px]"
                        autoFocus
                      />
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => handleSaveEdit(note.id)}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-xs"
                          onClick={() => setEditingNoteId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">{note.content}</p>
                  )}
                  
                  {/* Note Footer */}
                  {!isEditing && (
                    <div className="flex items-center justify-between mt-2 pt-1 border-t border-slate-700">
                      <span className="text-[10px] text-slate-500">
                        {note.author_name}
                      </span>
                      
                      {(note.author_id === currentUserId || note.author_id === 'ai-copilot') && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 text-slate-500 hover:text-slate-300"
                            onClick={() => handleEditNote(note)}
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 text-slate-500 hover:text-red-400"
                            onClick={() => handleDeleteNote(note.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
      
      {/* Add Note Input */}
      <div className="p-2 border-t border-slate-800 space-y-2">
        {/* Note Type Selector */}
        <div className="flex items-center gap-1">
          {NOTE_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <Button
                key={type.value}
                variant={noteType === type.value ? 'default' : 'ghost'}
                size="sm"
                className={`h-7 text-xs px-2 ${
                  noteType === type.value ? type.color : 'text-slate-400'
                }`}
                onClick={() => setNoteType(type.value)}
              >
                <Icon className="h-3 w-3 mr-1" />
                {type.label}
              </Button>
            );
          })}
        </div>
        
        {/* Input Area */}
        <div className="flex gap-2">
          <Textarea
            ref={textareaRef}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder={`Add ${getNoteTypeConfig(noteType).label.toLowerCase()} note...`}
            className="flex-1 text-sm bg-slate-800 border-slate-700 min-h-[60px] resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSubmitNote();
              }
            }}
          />
          <Button
            className="h-full px-3"
            onClick={handleSubmitNote}
            disabled={!newNote.trim() || isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        
        <p className="text-[10px] text-slate-500 text-center">
          Press ⌘+Enter to submit
        </p>
      </div>
    </div>
  );
};

export default L10VideoNotes;
